import { Button, HeaderContainer, Logo, LogoContainer, OptionsContainer, TopRow } from './Header.styles';
import { CloseIcon, FullScreenIcon } from '../../assets';
import { Fragment, FunctionalComponent, h } from 'preact';
import Heading from '../Heading';
import LanguageSelector from '../../components/LanguageSelector';
import { LogoCustomization } from '@usercentrics/cmp-browser-sdk';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { memo } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import { useGlobalStateContext } from '../../contexts/GlobalContext';
import { useKeyPress } from '../../hooks/useKeyPress';

type Props = {
  closeAriaLabel: string;
  fullScreenAriaLabel: string;
  logo: LogoCustomization;
  onClose?: () => void;
  onFullScreen?: () => void;
  onLanguageChange: (selectedLanguage: string) => void;
  showCloseButton?: boolean;
  showFullScreenButton?: boolean;
  showLanguage?: boolean;
  title: string;
};

const Header: FunctionalComponent<Props> = memo(
  ({
    closeAriaLabel,
    children,
    fullScreenAriaLabel,
    logo,
    onClose,
    onFullScreen,
    onLanguageChange,
    showCloseButton = false,
    showFullScreenButton,
    showLanguage = false,
    title,
  }) => {
    const isEscKeyPressed = useKeyPress({ targetKey: 'Escape' });
    const { langSelectorOpen } = useGlobalStateContext();

    // This will let users have a shortcut to close the second layer using the keyboard
    useEffect(() => {
      if (isEscKeyPressed && showCloseButton && onClose && !langSelectorOpen) {
        onClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEscKeyPressed, onClose, showCloseButton]);

    return (
      <HeaderContainer data-testid="uc-header" tabIndex={0}>
        {(logo.url || showLanguage || showCloseButton || showFullScreenButton) && (
          <TopRow>
            <LogoContainer position={logo.position}>
              {logo.url &&
                (logo.altTag ? (
                  <Logo position={logo.position} src={logo.url} alt={logo.altTag} />
                ) : (
                  <Logo position={logo.position} src={logo.url} aria-hidden="true" alt="" />
                ))}
            </LogoContainer>
            <OptionsContainer position={logo.position}>
              {showLanguage && <LanguageSelector onChange={onLanguageChange} />}
              {showCloseButton && (
                <Button aria-label={closeAriaLabel} height="28px" data-testid="uc-close-button" onClick={onClose}>
                  <CloseIcon />
                </Button>
              )}
              {showFullScreenButton && (
                <Button
                  aria-label={fullScreenAriaLabel}
                  height="24px"
                  data-testid="uc-fullscreen-button"
                  onClick={onFullScreen}
                >
                  <FullScreenIcon />
                </Button>
              )}
            </OptionsContainer>
          </TopRow>
        )}
        {logo.url && <VerticalSpacer large />}
        {title && title !== '' && (
          <Fragment>
            <Heading>{title}</Heading>
            <VerticalSpacer small />
          </Fragment>
        )}
        {children}
      </HeaderContainer>
    );
  },
);

export default Header;
